// Where a crash happened -- the ONE stack parser, read by both crash consumers.
//
// `stackLines` reads each line of a JS stack or a React component stack once; two views come out of it:
//   - the DEVICE record (crashCapture.ts; the crash screen's Copy details): `function @ file:line:col` frames with
//     the URL's origin and path dropped, and component names only;
//   - ANALYTICS (operation_failed, promise_rejection_unhandled) -- things about OUR CODE, never the user's data:
//       - error_name: the constructor name (TypeError, RangeError, ...) from a closed shape, else `other`;
//       - crash_frames / component_frames: the top frames of the JS stack and of React's component stack as
//         `<bundle chunk>:<line>:<col>` -- deployed assets only (other origins, extensions and inline code are
//         skipped). Map them back with the build's source maps (vite `sourcemap: true`);
//       - error_site: the innermost app component, identifier-shaped (crashSite).
//     The message is NEVER read for analytics: it can carry file names, markup text or URLs.
import { classifyError, type ErrorKind } from '@/analytics/errorKind';

// V8: "at fn (url:1:2)" / "at url:1:2"; WebKit/Gecko: "fn@url:1:2"
const FRAME = /^(?:at\s+)?(?:(.*?)\s*\(|(.*?)@)?([a-z]+:\/\/[^\s)]+|[a-z]:[/][^\s)]+|\/[^\s)]+)\)?$/i;
const ASSET_FRAME = /\/assets\/([\w.-]+\.m?js)(?:\?[^:\s)]*)?:(\d+):(\d+)/g;
const COMPONENT = /^(?:at\s+)?([A-Za-z_$][\w$.]*)/;
const IDENT = /^[A-Za-z_$][\w$]{0,59}$/;

/** One stack line, read once: its device frame, its deployed-asset locations, its component name. */
export type StackLine = { frame: string | null; assets: string[]; component: string | null };

function frameOf(s: string): string | null {
    if (!s) return null;
    const m = s.match(FRAME);
    if (!m) return null; // the 'Name: message' header line, or anything that is not a frame
    const fn = (m[1] ?? m[2] ?? '').trim() || '<anon>';
    const loc = m[3].replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/\?[^:]*/, '');
    const file = loc.split('/').pop() ?? loc;
    return `${fn.slice(0, 60)} @ ${file}`;
}

function componentOf(s: string): string | null {
    const m = s.match(COMPONENT);
    if (!m) return null;
    const name = m[1].split('.').pop()!;
    return IDENT.test(name) ? name : null;
}

/** The one stack parser: each line of a JS stack or React component stack. */
export function stackLines(text: string): StackLine[] {
    return text.split('\n').map((raw) => {
        const s = raw.trim();
        return {
            frame: frameOf(s),
            assets: Array.from(s.matchAll(ASSET_FRAME), (m) => `${m[1]}:${m[2]}:${m[3]}`),
            component: componentOf(s),
        };
    });
}

/** Device record: the top 10 frames, `fn @ file:line:col` (a line equal to the message is not one). */
export function deviceFrames(stack: string | undefined, message: unknown): string[] {
    return stackLines(stack ?? '')
        .map((l) => l.frame)
        .filter((f): f is string => !!f && f !== message)
        .slice(0, 10);
}

/** Device record: the component names of a React component stack (at most 12). */
export function componentNames(componentStack: string | null | undefined): string[] {
    if (!componentStack) return [];
    return stackLines(componentStack)
        .map((l) => l.component)
        .filter((c): c is string => c !== null)
        .slice(0, 12);
}

/** The innermost app component of a crash, identifier-shaped, for analytics (never raw text). */
export function crashSite(components: string[]): string {
    const c = components.find((n) => /^[A-Z]/.test(n) && !/^(Suspense|Fragment|StrictMode|Provider|Consumer)$/.test(n));
    return c && IDENT.test(c) ? c : 'unknown';
}

/** Top `max` deployed-asset frames of a stack, `a.js:1:2|b.js:3:4`, or `none`. */
export function codeFrames(stack: unknown, max = 3): string {
    if (typeof stack !== 'string' || !stack) return 'none';
    const out = stackLines(stack).flatMap((l) => l.assets).slice(0, max);
    return out.length ? out.join('|') : 'none';
}

/** The error's constructor name if it looks like one, else `other` / `non-error`. */
export function errorName(err: unknown): string {
    if (!(err instanceof Error)) return 'non-error';
    return /^[A-Z][A-Za-z]{0,40}(Error|Exception)$/.test(err.name) ? err.name : 'other';
}

/** The operation_failed props for a caught render crash. */
export function crashProps(err: unknown, componentStack?: string | null): {
    error_name: string;
    crash_frames: string;
    component_frames: string;
} {
    return {
        error_name: errorName(err),
        crash_frames: codeFrames(err instanceof Error ? err.stack : undefined),
        component_frames: codeFrames(componentStack ?? undefined),
    };
}

export type RejectionProps = { error_kind: ErrorKind; error_name: string; crash_frames: string };

/** The promise_rejection_unhandled props: a closed slug and code locations only. */
export function rejectionProps(reason: unknown): RejectionProps {
    return {
        error_kind: classifyError(reason),
        error_name: errorName(reason),
        crash_frames: codeFrames(reason instanceof Error ? reason.stack : undefined),
    };
}
